import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { path: "/", label: "Home" },
  { path: "/services", label: "Services" },
  { path: "/pricing", label: "Pricing" },
  { path: "/gallery", label: "Gallery" },
  { path: "/junior-dj-academy", label: "Junior DJ Academy" },
  { path: "/lessons", label: "DJ & Production Lessons" },
  { path: "/dj-memberships", label: "DMRG Sessions" },
  { path: "/about", label: "About" },
  { path: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [showUrgencyBar, setShowUrgencyBar] = useState(false);
  const location = useLocation();

  // Check if urgency bar is visible (only on mount and when visibility might change)
  useEffect(() => {
    const checkUrgencyBar = () => {
      const dismissed = sessionStorage.getItem("urgencyBar_dismissed");
      setShowUrgencyBar(!dismissed);
    };
    
    checkUrgencyBar();
    
    // Listen for storage changes instead of polling
    window.addEventListener('storage', checkUrgencyBar);
    return () => window.removeEventListener('storage', checkUrgencyBar);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <>
      <nav className={`fixed left-0 right-0 z-50 glass transition-all duration-300 ${showUrgencyBar ? 'top-7 sm:top-6' : 'top-0'}`} data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-28">
            {/* Logo */}
            <Link to="/" className="flex items-center z-50" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/w4q79k1x_Dream%20Maker-4.png" 
                alt="Dream Makers Recording Group"
                className="h-10 sm:h-20 md:h-24 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center gap-4 2xl:gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-dream-violet ${
                    location.pathname === link.path ? "text-dream-violet" : "text-white/80"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Book Now Button - Desktop */}
            <div className="hidden xl:block">
              <Link to="/booking">
                <Button 
                  data-testid="book-now-btn"
                  className="bg-white text-black hover:bg-dream-violet hover:text-white rounded-full px-6 font-semibold transition-all duration-300"
                >
                  Book Now
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="xl:hidden text-white p-2 z-[60] relative"
              data-testid="mobile-menu-btn"
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              {isOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu - Full Screen Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden fixed inset-0 bg-dream-bg z-[55]"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center justify-center min-h-screen px-6">
              <nav className="flex flex-col items-center gap-8">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 + 0.1 }}
                  >
                    <Link
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                      className={`text-3xl font-heading font-bold transition-colors ${
                        location.pathname === link.path 
                          ? "text-dream-violet" 
                          : "text-white hover:text-dream-violet"
                      }`}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
              </nav>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-12"
              >
                <Link to="/booking" onClick={() => setIsOpen(false)}>
                  <Button 
                    className="bg-dream-violet text-white hover:bg-dream-violet-glow rounded-full px-12 py-6 text-lg font-bold"
                    data-testid="mobile-book-now-btn"
                  >
                    Book Now
                  </Button>
                </Link>
              </motion.div>

              {/* Contact Info on Mobile */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-16 text-center"
              >
                <div className="space-y-1">
                  <a href="tel:0421815593" className="text-white/60 text-sm hover:text-dream-violet transition-colors block">
                    0421 815 593
                  </a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
